// src/pages/Artistes.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Mousewheel } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import { db } from "../firebase";
import { doc, onSnapshot } from "firebase/firestore";
import CoverSliderArtistes from "../components/CoverSliderArtistes"; // ✅ nouveau slider artistes
import { mergeSiteContent } from "../utils/helpers";

const DEFAULT = {
  pageStatus: "active",
  unavailableMessage: "Cette page artiste est momentanément indisponible.",
  slide1: {
    titleTop: "YMS",
    subtitleTop: "Artiste, beatmaker, ingenieur son",
    image: "/images/yms-full.png",
  },
  slide2: { video: "/videos/studio-video.mp4", caption: "Studio, vision, discipline." },
  slide3: {
    heading: "Biographie",
    text:
      "YMS construit un univers entre rap, production et direction sonore. Forme par le studio, il avance avec une approche complete: ecriture, beatmaking, prise de voix, mix et image.",
  },
  slide4: {
    titleTop: "Unlimited Artist",
    caption: "Une identite en mouvement",
    image: "/images/icon-unlimited-artist.png",
  },
  gallery: ["/images/yms2.png", "/images/yms3.png", "/images/yms4.png"],
  ymsSection: {
    title: "YMS devoile son univers",
    text:
      "Une signature visuelle sombre, une culture studio forte et une envie claire: faire exister chaque sortie comme un monde complet.",
  },
  covers: [], // ✅ ajout covers
};

export default function Artistes() {
  const [data, setData] = useState(DEFAULT);
  const [site, setSite] = useState(() => mergeSiteContent());
  const [loading, setLoading] = useState(true);
  const [currentImage, setCurrentImage] = useState(0);
  const [videoError, setVideoError] = useState(false);

  useEffect(() => {
    const ref = doc(db, "admin", "artistes");
    const off = onSnapshot(
      ref,
      (snap) => {
        const d = snap.data() || {};
        setData({
          pageStatus: d.pageStatus || DEFAULT.pageStatus,
          unavailableMessage: d.unavailableMessage || DEFAULT.unavailableMessage,
          slide1: { ...DEFAULT.slide1, ...(d.slide1 || {}) },
          slide2: { ...DEFAULT.slide2, ...(d.slide2 || {}) },
          slide3: { ...DEFAULT.slide3, ...(d.slide3 || {}) },
          slide4: { ...DEFAULT.slide4, ...(d.slide4 || {}) },
          gallery: Array.isArray(d.gallery) && d.gallery.length ? d.gallery : DEFAULT.gallery,
          ymsSection: { ...DEFAULT.ymsSection, ...(d.ymsSection || {}) },
          covers: d.covers || [], // ✅ ajout covers
        });
        setLoading(false);
      },
      (err) => {
        console.error("Erreur Firestore Artistes:", err);
        setData(DEFAULT);
        setLoading(false);
      }
    );
    return () => off();
  }, []);

  useEffect(() => {
    const off = onSnapshot(
      doc(db, "admin", "content"),
      (snap) => setSite(mergeSiteContent(snap.exists() ? snap.data() : {})),
      (err) => console.error("Erreur Firestore contenu artistes:", err)
    );
    return () => off();
  }, []);

  // défilement auto toutes les 4 sec pour gallery
  useEffect(() => {
    if (!data.gallery || data.gallery.length === 0) return;
    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % data.gallery.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [data.gallery]);

  const videoKey = useMemo(() => `v-${data.slide2.video}`, [data.slide2.video]);

  useEffect(() => {
    setVideoError(false);
  }, [data.slide2.video]);

  if (!loading && data.pageStatus !== "active") {
    return (
      <main className="grid min-h-[78vh] place-items-center bg-black px-6 py-24 text-white">
        <section className="max-w-2xl rounded-lg border border-white/10 bg-zinc-950 p-8 text-center md:p-12">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-red-500">
            Artiste
          </p>
          <h1 className="mt-4 text-4xl font-black uppercase md:text-6xl">
            Page momentanément indisponible
          </h1>
          <p className="readable-copy mt-5 text-lg text-gray-300">
            {data.unavailableMessage || DEFAULT.unavailableMessage}
          </p>
        </section>
      </main>
    );
  }

  return (
    <main className="bg-[#101010] text-white min-h-screen overflow-x-hidden">
      <section className="px-6 pb-10 pt-24 md:px-20 md:pt-32">
        <div className="mx-auto max-w-7xl">
          <p className="mb-4 text-sm font-semibold uppercase tracking-[0.25em] text-red-500">
            Artistes
          </p>
          <h1 className="max-w-4xl text-5xl font-black uppercase leading-none md:text-8xl">
            Les visages derriere le son.
          </h1>
        </div>
      </section>

      <section className="pb-12">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* ======= SLIDES EXISTANTS ======= */}
        {!loading && site.visibility.artistesSlides && (
          <Swiper
            modules={[Pagination, Mousewheel]}
            slidesPerView={1.03}
            breakpoints={{
              768: { slidesPerView: 1.12, spaceBetween: 24 },
              1024: { slidesPerView: 1.18, spaceBetween: 28 },
            }}
            centeredSlides
            spaceBetween={14}
            mousewheel={{ forceToAxis: true, sensitivity: 0.8 }}
            pagination={{ clickable: true }}
            className="swiper-apple w-full"
            style={{ overflow: "visible" }}
          >
            {/* Slide 1 */}
            <SwiperSlide>
              <div className="bg-black rounded-lg border border-white/10 h-[520px] md:h-[600px] px-6 md:px-10 pt-10 pb-8 flex flex-col items-center">
                <div className="text-center mb-6 md:mb-8">
                  {data.slide1.titleTop && (
                    <p className="text-3xl md:text-4xl font-bold text-white">
                      {data.slide1.titleTop}
                    </p>
                  )}
                  {data.slide1.subtitleTop && (
                    <p className="text-xl md:text-2xl text-gray-300 mt-2">
                      {data.slide1.subtitleTop}
                    </p>
                  )}
                </div>
                {data.slide1.image ? (
                  <img
                    src={data.slide1.image}
                    alt="Slide 1"
                    className="max-h-[360px] md:max-h-[400px] object-contain"
                  />
                ) : (
                  <div className="h-[360px] w-full grid place-items-center text-white/40">
                    Image en preparation
                  </div>
                )}
              </div>
            </SwiperSlide>

            {/* Slide 2 — Vidéo */}
            <SwiperSlide>
              <div className="relative bg-black rounded-lg border border-white/10 h-[520px] md:h-[600px] overflow-hidden flex flex-col">
                <div className="flex-1">
                  {data.slide2.video && !videoError ? (
                    <video
                      key={videoKey}
                      src={data.slide2.video}
                      className="h-full w-full object-cover"
                      autoPlay
                      loop
                      muted
                      playsInline
                      preload="metadata"
                      onError={() => setVideoError(true)}
                    />
                  ) : (
                    <div className="h-full w-full grid place-items-center text-white/40">
                      {videoError ? "Video illisible, remplace le fichier dans l'admin" : "Video en preparation"}
                    </div>
                  )}
                </div>
                {data.slide2.caption && (
                  <div className="absolute bottom-0 left-0 w-full bg-gradient-to-t from-black/70 to-transparent p-4 text-center text-2xl font-semibold">
                    {data.slide2.caption}
                  </div>
                )}
              </div>
            </SwiperSlide>

            {/* Slide 3 — Biographie */}
            <SwiperSlide>
              <div className="bg-black rounded-lg border border-white/10 h-[520px] md:h-[600px] p-6 md:p-10 flex flex-col">
                {data.slide3.heading && (
                  <h3 className="text-2xl md:text-3xl font-semibold text-center mb-4">
                    {data.slide3.heading}
                  </h3>
                )}
                <div className="flex-1 overflow-y-auto pr-2">
                  <p className="readable-copy text-base md:text-lg max-w-3xl mx-auto">
                    {data.slide3.text || DEFAULT.slide3.text}
                  </p>
                </div>
              </div>
            </SwiperSlide>

            {/* Slide 4 */}
            <SwiperSlide>
              <div className="bg-black rounded-lg border border-white/10 h-[520px] md:h-[600px] px-6 md:px-10 pt-10 pb-8 flex flex-col items-center">
                <div className="text-center mb-6 md:mb-8">
                  {data.slide4.titleTop && (
                    <p className="text-3xl md:text-4xl font-bold text-white">
                      {data.slide4.titleTop}
                    </p>
                  )}
                  {data.slide4.caption && (
                    <p className="text-xl md:text-2xl text-gray-300 mt-2">
                      {data.slide4.caption}
                    </p>
                  )}
                </div>
                {data.slide4.image ? (
                  <img
                    src={data.slide4.image}
                    alt="Slide 4"
                    className="max-h-[360px] md:max-h-[400px] object-contain"
                  />
                ) : (
                  <div className="h-[360px] w-full grid place-items-center text-white/40">
                    Image en preparation
                  </div>
                )}
              </div>
            </SwiperSlide>
          </Swiper>
        )}
      </div>
      </section>

      {/* ===== SECTION YMS ===== */}
      {site.visibility.artistesGallery && data.gallery && data.gallery.length > 0 && (
        <section className="relative bg-black text-white overflow-hidden flex flex-col md:flex-row items-center justify-between px-6 py-20 md:px-24">
          {/* Texte à gauche */}
          <div className="z-10 max-w-xl mb-10 md:mb-0">
            {data.ymsSection?.title && (
              <h2 className="text-4xl md:text-6xl font-extrabold leading-tight mb-4">
                {data.ymsSection.title}
              </h2>
            )}
            {data.ymsSection?.text && (
              <p className="readable-copy text-lg text-gray-300 mb-6">
                {data.ymsSection.text}
              </p>
            )}
            <a
              href="/contact#contact-form"
              className="inline-flex rounded-full bg-white px-6 py-3 text-sm font-bold uppercase tracking-wide text-black transition hover:bg-red-600 hover:text-white"
            >
              Proposer un projet
            </a>
          </div>

          {/* Diaporama à droite */}
          <div className="w-full md:w-1/2 relative z-0">
            <div className="w-full h-[500px] md:h-[700px] relative flex items-center justify-center bg-black rounded-lg overflow-hidden">
              {data.gallery.map((img, index) => (
                <img
                  key={index}
                  src={img}
                  alt={`YMS ${index}`}
                  className={`absolute inset-0 mx-auto max-h-full max-w-full object-contain rounded-2xl transition-opacity duration-1000 ease-in-out ${
                    index === currentImage ? "opacity-100" : "opacity-0"
                  }`}
                  style={{
                    maskImage:
                      "linear-gradient(to left, rgba(0,0,0,1) 85%, rgba(0,0,0,0))",
                    WebkitMaskImage:
                      "linear-gradient(to left, rgba(0,0,0,1) 85%, rgba(0,0,0,0))",
                  }}
                />
              ))}
            </div>

            {/* Indicateurs */}
            <div className="flex justify-center mt-4 gap-2">
              {data.gallery.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImage(index)}
                  className={`w-3 h-3 rounded-full transition-all ${
                    currentImage === index
                      ? "bg-white w-6"
                      : "bg-gray-500 hover:bg-gray-400"
                  }`}
                  aria-label={`Afficher l'image ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ===== SECTION COVERS (nouveau slider artistes) ===== */}
      {site.visibility.artistesCovers && data.covers && data.covers.length > 0 && (
        <div className="mt-20">
          <CoverSliderArtistes /> {/* ✅ utilise le même design que homepage */}
        </div>
      )}

      {/* Bullets Apple style */}
      <style>{`
        .swiper-apple .swiper-pagination { position: relative; margin-top: 18px; }
        .swiper-apple .swiper-pagination-bullet {
          width: 8px; height: 8px; border-radius: 9999px; background: #4b5563; opacity: 1;
          transition: width .25s ease, background-color .25s ease; margin: 0 6px !important;
        }
        .swiper-apple .swiper-pagination-bullet-active { width: 28px; background: #ef4444; }
      `}</style>
    </main>
  );
}
